<template>
  <a
    role="button"
    class="col-thumb p-2"
    :style="{ height, width }"
    :class="{ selected: isSelected, assigned: isAssigned }"
    :title="title"
    @click="handleClick"
  >
    <img
      v-if="isImage && url"
      :src="getUrl"
      class="rs-gallery-item-img rs-rounded"
      loading="lazy"
    >

    <div
      v-else
      class="rs-rounded rs-file-thumb"
    >
      <div class="text-center mb-1 thumb-icon">
        <i class="fa fa-file" />
      </div>
      <div class="text-center">
        <small
          class="d-block text-truncate"
          :style="{ maxWidth: `${(parseFloat(width) - 2.5)}rem` }"
        >
          {{ fileDisplayName }}
        </small>
      </div>
    </div>

    <div
      v-show="isAssignedSwitchVisible"
    >
      <RsFormSwitch
        :id="`switch_${getUrl}`"
        :model-value="inputAssigned"
        :true-value="true"
        :false-value="false"
        class="assigned-switch"
        title="Toggle image/file visibility on your website(s)"
        @change="update"
      />

      <RsFavorite
        v-show="isFavorable"
        icon="star"
        :value="favoriteValue ? 1 : 0"
        class="assigned-fav"
        :true-value="1"
        :false-value="0"
        @click.native="$emit('on-toggle-favorite', data);"
      />

      <!-- Featured badge -->
      <div
        v-show="isFavorable"
        class="featured"
      >
        <RsBadge
          v-show="favoriteValue"
          pill
          variant="dark"
        >Featured Image</RsBadge>

      </div>
    </div>
    <div
      v-if="alwaysShowName"
      class="media-name rs-rounded m-2"
    >
      {{ fileDisplayName }}
    </div>

    <div
      v-show="isOverlayVisible"
      class="overlay rs-rounded m-2"
      :title="title"
      :style="{ ...!isOverlayHoverOnly && { opacity: '1' } }"
    >
      <div class="text-center">
        <i :class="`h5 m-0 fas ${overlayIcon}`" />
        <p
          v-show="overlayText"
          class="font-weight-bold"
        >{{ overlayText }}</p>
      </div>
    </div>
  </a>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent<any, any, any, any>({
  name: 'RsGalleryItem',

  props: {
    url: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    fileDisplayName: {
      type: String,
      default: '',
    },
    data: {
      type: Object,
      default: () => null,
    },
    overlayIcon: {
      type: String,
      default: '',
    },
    overlayText: {
      type: String,
      default: '',
    },
    isImage: {
      type: Boolean,
    },
    isFavorable: {
      type: Boolean,
      default: false,
    },
    favoriteValue: {
      type: Number,
      default: 0,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isAssigned: {
      type: Boolean,
      default: false,
    },
    isAssignedSwitchVisible: {
      type: Boolean,
      default: false,
    },
    isOverlayVisible: {
      type: Boolean,
      default: false,
    },
    isOverlayHoverOnly: {
      type: Boolean,
      default: true,
    },
    height: {
      type: String,
      default: '9rem',
    },
    width: {
      type: String,
      default: '9rem',
    },
    useXhr: {
      type: Boolean,
      default: false,
    },
    totalFiles: {
      type: Number,
      default: 0,
    },
    imageRenderKey: {
      type: String,
      default: '',
    },
    alwaysShowName: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      inputAssigned: !!this.isAssigned,
      busy: false,
    };
  },

  computed: {
    getUrl() {
      let querystring = '?';

      if (this.useXhr) {
        querystring += '&x-request=xhr';
      }

      if (this.imageRenderKey) {
        querystring += `&key=${this.imageRenderKey}`;
      }

      if (querystring === '?') {
        querystring = '';
      }

      return (`${this.url}${(querystring.replace('?&', '?'))}`);
    },
  },

  watch: {
    totalFiles() {
      this.inputAssigned = this.isAssigned;
    },
  },

  methods: {
    update() {
      this.inputAssigned = !this.inputAssigned;
      this.$emit('on-toggle-assigned', this.inputAssigned);
    },
    handleClick() {
      this.$emit('click', this.data);
    },
  },
});
</script>

<style lang="scss" scoped>
  @import '@/styles/_variables.scss';

  $transitionDelay: 0.2s;

  .col-thumb {
    color: $gray-700;
    display: inline-block;
    vertical-align: top;
    position: relative;

    &.selected {
      img, .rs-file-thumb {
        border: solid 2px $info;
        transition: all 0.2s ease;
      }
    }

    .assigned-switch {
      position: absolute;
      z-index: 2;
      top: 0.75rem;
      right: 0.75rem;
    }

    .assigned-fav{
      position: absolute;
      z-index: 2;
      top: 0.75rem;
      left: 0.75rem;
    }

    .overlay {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      overflow: hidden;
      display: flex;
      height: calc(100% - 1rem);
      align-items: center;
      justify-content: center;
      background: rgba($tertiary-dark, 0.45);
      transition: opacity $transitionDelay ease;
      z-index: 1;
      color: $primary-color;
      opacity: 0;

      * {
        color: $white;
      }
    }
    ::v-deep .rs-favorite__icon--active{
      color: gold
    }

    .media-name {
      position: absolute;
      left: 0.25rem;
      right: 0.25rem;
      bottom: 0.25rem;
      line-height: 1.2;
      padding: 0.25rem 0.5rem;
      font-size: 0.85rem;
      font-weight: bolder;
      color: $white;
      background: rgba($tertiary-dark, 0.75);
    }
    .featured {
      position: absolute;
      width: calc(100% - 1rem);
      text-align: center;
      bottom: 0;
      height:25%;
      ::v-deep .rs-badge {
        font-size: 0.6rem;
      }
  }

    .rs-gallery-item-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border: $card-border;
      background: url('/img/default_300x300.png') no-repeat center center;
      background-size: contain;
    }

    &:hover .overlay {
      opacity: 1;
    }

    &.rs-thumb-file-upload {
      .overlay {
        opacity: 1;
      }
      &.uploaded .overlay {
        opacity: 0;
      }
    }

    .rs-file-thumb {
      background: $gray-200;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      flex-direction: column;
      align-items: center;

      .thumb-icon {
        font-size: 2.5rem;
      }
    }
  }
</style>
